import React, { useState } from 'react';
import { Box, Button, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Typography, Chip, IconButton, TablePagination } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditIcon from '@mui/icons-material/Edit';
import RecordAttendanceModal from '../../../components/RecordAttendanceModal';
import EditAttendanceModal from '../../../components/EditAttendanceModal';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { exportClientSide } from '../../../utils/exportUtils';
import { extractTotalCount, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function AttendanceLogTab({
    handleSuccess,
    staffMembers
}) {
    const [localAttendanceRecords, setLocalAttendanceRecords] = useState([]);
    const [totalAttendanceRecords, setTotalAttendanceRecords] = useState(0);
    const [localIsLoading, setLocalIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isRecordAttendanceOpen, setIsRecordAttendanceOpen] = useState(false);
    const [isEditAttendanceOpen, setIsEditAttendanceOpen] = useState(false);
    const [selectedAttendance, setSelectedAttendance] = useState(null);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchPaginatedAttendance = async () => {
        setLocalIsLoading(true);
        try {
            const { StaffService } = await import('../../../api/services/staffService');
            const data = await StaffService.getAttendance({ page, limit: rowsPerPage });
            const dataArray = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setLocalAttendanceRecords(dataArray);
            setTotalAttendanceRecords(extractTotalCount(data, dataArray));
        } catch (error) {
            console.error("Failed to fetch paginated attendance:", error);
        } finally {
            setLocalIsLoading(false);
        }
    };

    React.useEffect(() => {
        fetchPaginatedAttendance();
    }, [page, rowsPerPage]);

    const handleEditAttendance = (attendance) => {
        setSelectedAttendance(attendance);
        setIsEditAttendanceOpen(true);
    };

    const getAttendanceStatusColor = (status) => {
        switch (status) {
            case 'Present': return 'success';
            case 'Late': return 'warning';
            case 'Absent': return 'error';
            default: return 'default';
        }
    };

    const handleExport = (format) => {
        setExportModalOpen(false);
        try {
            exportClientSide(format, localAttendanceRecords, 'attendance_log');
            handleSuccess('Export successful.');
        } catch (error) {
            handleSuccess('Export failed.');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
                <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} sx={standardSecondaryButtonSx} onClick={() => setExportModalOpen(true)}>
                    Export Options
                </Button>
                <ExportFormatModal
                    open={exportModalOpen}
                    onClose={() => setExportModalOpen(false)}
                    onExport={handleExport}
                    isExporting={false}
                    title="Export Attendance Log"
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsRecordAttendanceOpen(true)}>
                    Record Attendance
                </Button>
            </Box>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Staff Member</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Clock In</TableCell>
                            <TableCell>Clock Out</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {localIsLoading ? (
                            [...Array(5)].map((_, index) => (
                                <TableRow key={`skeleton-att-${index}`}>
                                    <TableCell colSpan={6}><Skeleton variant="rectangular" height={40} sx={{ my: 1, borderRadius: 1 }} /></TableCell>
                                </TableRow>
                            ))
                        ) : localAttendanceRecords.length > 0 ? (
                            localAttendanceRecords.map((a) => (
                                <TableRow key={a.id || a._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{a.staff?.fullName || a.staffName || a.staff || 'Unknown'}</Typography>
                                        {a.staff?.employeeId && <Typography variant="caption" color="text.secondary">{a.staff.employeeId}</Typography>}
                                    </TableCell>
                                    <TableCell>{a.date || a.attendanceDate || '--'}</TableCell>
                                    <TableCell>{a.clockInTime ? new Date(a.clockInTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : (a.clockIn || a.checkIn || '--:--')}</TableCell>
                                    <TableCell>{a.clockOutTime ? new Date(a.clockOutTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : (a.clockOut || a.checkOut || '--:--')}</TableCell>
                                    <TableCell><Chip label={a.status || 'Present'} size="small" color={getAttendanceStatusColor(a.status || 'Present')} /></TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleEditAttendance(a)} title="Edit Attendance">
                                            <EditIcon fontSize="small" color="primary" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">No attendance records found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalAttendanceRecords}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <RecordAttendanceModal
                open={isRecordAttendanceOpen}
                onClose={() => setIsRecordAttendanceOpen(false)}
                onSuccess={handleSuccess}
                staffMembers={staffMembers}
            />

            <EditAttendanceModal
                open={isEditAttendanceOpen}
                onClose={() => { setIsEditAttendanceOpen(false); setSelectedAttendance(null); }}
                onSuccess={handleSuccess}
                attendanceRecord={selectedAttendance}
            />
        </Box>
    );
}
